/**
 * Pin-style cone marker — exact 3DRoad look (ui/src/viewer/markers.js).
 *
 * Stack of meshes:
 *   - dark outline cone (slightly bigger, behind)
 *   - inner cone in `color`
 *   - small green cap sphere on top
 *   - tiny white highlight dot
 *
 * Tip of the cone sits at world `position`; base points up (+Z).
 */

import {
  ConeGeometry,
  Group,
  Mesh,
  MeshBasicMaterial,
  SphereGeometry,
  Vector3,
} from 'three'

export function buildMarkerCone(
  position: Vector3,
  color: number,
  /** Overall world-size of the cone height; tweak per scene scale. */
  radius: number = 0.6,
): Group {
  const group = new Group()
  group.position.copy(position)
  // Stamp so `collectPoints` in viewer/annotations.ts can recover the
  // original click positions when serializing a Measurement to the wire.
  group.userData.isAnnotationVertex = true

  const pinH = radius * 1.4
  const pinR = radius * 0.12
  const capR = radius * 0.18

  // Dark outline cone (slightly wider)
  const outGeo = new ConeGeometry(pinR * 1.6, pinH * 1.05, 12)
  const outMat = new MeshBasicMaterial({
    color: 0x1a1a1a,
    depthTest: false,
    transparent: true,
  })
  const outCone = new Mesh(outGeo, outMat)
  outCone.rotation.x = -Math.PI / 2
  outCone.position.set(0, 0, (pinH * 1.05) / 2)
  outCone.renderOrder = 1004
  group.add(outCone)

  // Inner pin
  const pinGeo = new ConeGeometry(pinR, pinH, 12)
  const pinMat = new MeshBasicMaterial({
    color,
    depthTest: false,
    transparent: true,
  })
  const pin = new Mesh(pinGeo, pinMat)
  pin.rotation.x = -Math.PI / 2
  pin.position.set(0, 0, pinH / 2)
  pin.renderOrder = 1005
  group.add(pin)

  // Green cap on top
  const capGeo = new SphereGeometry(capR, 10, 10)
  const capMat = new MeshBasicMaterial({
    color: 0x04a823,
    depthTest: false,
    transparent: true,
  })
  const cap = new Mesh(capGeo, capMat)
  cap.position.set(0, 0, pinH + capR * 0.5)
  cap.renderOrder = 1006
  group.add(cap)

  // White highlight dot
  const dotR = capR * 0.4
  const dotGeo = new SphereGeometry(dotR, 6, 6)
  const dotMat = new MeshBasicMaterial({
    color: 0xffffff,
    depthTest: false,
    transparent: true,
  })
  const dot = new Mesh(dotGeo, dotMat)
  dot.position.set(0, 0, pinH + capR * 0.9)
  dot.renderOrder = 1007
  group.add(dot)

  return group
}
